// Banner de promociones arriba del menú público (plan Pro). Se guardan en el campo `promos`
// del negocio (users y la copia pública menus/), en el orden en que se muestran:
// {
//   id, titulo, texto,        // texto corto: "2x1 en pizzas", "Todos los martes"
//   imagen,                   // link de Storage, o "" (entonces va con el color del menú)
//   producto,                 // id de un producto para abrir al tocarla, o ""
//   dias,                     // días de la semana en que se ve (0 = domingo); vacío = todos
//   desde, hasta,             // fechas "AAAA-MM-DD" (inclusive), o ""
//   horaDesde, horaHasta,     // "HH:MM", o "" (si hasta < desde, cruza la medianoche)
//   activa                    // el dueño la puede pausar sin borrarla
// }
// Fechas, días y horas son de Argentina, igual que el "Abierto ahora" (hours.js).

export const MAX_PROMOS = 5;
export const PROMO_TITLE_MAX = 40;
export const PROMO_TEXT_MAX = 120;
// Cada cuánto pasa sola a la siguiente.
export const PROMO_SECONDS = 5;

export const PROMO_DAYS = [
    { value: 1, short: "Lu", name: "Lunes" },
    { value: 2, short: "Ma", name: "Martes" },
    { value: 3, short: "Mi", name: "Miércoles" },
    { value: 4, short: "Ju", name: "Jueves" },
    { value: 5, short: "Vi", name: "Viernes" },
    { value: 6, short: "Sá", name: "Sábado" },
    { value: 0, short: "Do", name: "Domingo" }
];

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const STORAGE_URL = /^https:\/\/firebasestorage\.googleapis\.com\/[^<>"' ]*$/;

// eslint-disable-next-line no-control-regex
const line = (value, max) => String(value ?? "").replace(/[<>\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim().slice(0, max);

// Promo con todos los campos y en formato, descartando lo raro.
export const cleanPromo = (p = {}) => ({
    id: line(p.id, 40) || Math.random().toString(36).slice(2, 10),
    titulo: line(p.titulo, PROMO_TITLE_MAX),
    texto: line(p.texto, PROMO_TEXT_MAX),
    imagen: typeof p.imagen === "string" && STORAGE_URL.test(p.imagen) && p.imagen.length <= 600 ? p.imagen : "",
    producto: line(p.producto, 60),
    dias: [...new Set((Array.isArray(p.dias) ? p.dias : []).filter((d) => Number.isInteger(d) && d >= 0 && d <= 6))].sort(),
    desde: DATE.test(p.desde || "") ? p.desde : "",
    hasta: DATE.test(p.hasta || "") ? p.hasta : "",
    horaDesde: TIME.test(p.horaDesde || "") ? p.horaDesde : "",
    horaHasta: TIME.test(p.horaHasta || "") ? p.horaHasta : "",
    activa: p.activa !== false
});

export const promosOf = (business) => (Array.isArray(business?.promos) ? business.promos : []).slice(0, MAX_PROMOS).map(cleanPromo);

// Lo que está mal en una promo antes de guardarla, o null.
export const promoProblem = (p) => {
    if (!p.titulo || p.titulo.length < 2) return "Escribí un título para la promo, por ejemplo \"2x1 en pizzas\".";
    if (p.desde && p.hasta && p.hasta < p.desde) return "La fecha de fin es anterior a la de inicio.";
    if (!!p.horaDesde !== !!p.horaHasta) return "Completá las dos horas (desde y hasta), o dejá las dos vacías.";
    if (p.horaDesde && p.horaDesde === p.horaHasta) return "La hora de inicio y la de fin son iguales.";
    return null;
}

// Fecha, día y minutos en Argentina (UTC−3).
const argentina = (date) => {
    const ar = new Date(date.getTime() - 3 * 60 * 60 * 1000);
    return {
        ymd: ar.toISOString().slice(0, 10),
        day: ar.getUTCDay(),
        minutes: ar.getUTCHours() * 60 + ar.getUTCMinutes()
    };
}

const toMinutes = (hhmm) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));

// Si la promo se ve en este momento.
export const promoLive = (p, date = new Date()) => {
    if (!p.activa || !p.titulo) return false;
    const now = argentina(date);
    if (p.desde && now.ymd < p.desde) return false;
    if (p.hasta && now.ymd > p.hasta) return false;
    if (p.horaDesde && p.horaHasta) {
        const from = toMinutes(p.horaDesde);
        const to = toMinutes(p.horaHasta);
        // Cruza la medianoche (ej. 22:00 a 02:00): de madrugada cuenta como el día anterior.
        if (from < to) {
            if (now.minutes < from || now.minutes >= to) return false;
        } else if (now.minutes < to) {
            return !p.dias.length || p.dias.includes((now.day + 6) % 7);
        } else if (now.minutes < from) {
            return false;
        }
    }
    return !p.dias.length || p.dias.includes(now.day);
}

// Si el plan del negocio muestra el banner.
export const promosAvailable = (business, proOnly) => !proOnly || business?.subscribed === true;

// Las promos que ve el cliente ahora: las del negocio que están en fecha y horario.
export const livePromos = (business, proOnly, date = new Date()) => promosAvailable(business, proOnly)
    ? promosOf(business).filter((p) => promoLive(p, date))
    : [];

// Resumen de cuándo se ve, para la lista del panel: "Martes y jueves · 18:00 a 20:00 · hasta el 30/11".
const fmtDate = (ymd) => `${Number(ymd.slice(8, 10))}/${Number(ymd.slice(5, 7))}`;
export const promoWhen = (p) => {
    const parts = [];
    if (p.dias.length && p.dias.length < 7) {
        const names = PROMO_DAYS.filter((d) => p.dias.includes(d.value)).map((d, i) => i ? d.name.toLowerCase() : d.name);
        parts.push(names.length > 1 ? names.slice(0, -1).join(", ") + " y " + names[names.length - 1] : names[0]);
    } else {
        parts.push("Todos los días");
    }
    if (p.horaDesde && p.horaHasta) parts.push(`${p.horaDesde} a ${p.horaHasta}`);
    if (p.desde && p.hasta) parts.push(`del ${fmtDate(p.desde)} al ${fmtDate(p.hasta)}`);
    else if (p.desde) parts.push(`desde el ${fmtDate(p.desde)}`);
    else if (p.hasta) parts.push(`hasta el ${fmtDate(p.hasta)}`);
    return parts.join(" · ");
}

// Estado para el panel: "En el menú ahora", "Pausada", "Vencida", "Programada" o "Fuera de horario".
export const promoState = (p, date = new Date()) => {
    if (!p.activa) return { label: "Pausada", live: false };
    if (promoLive(p, date)) return { label: "En el menú ahora", live: true };
    const today = argentina(date).ymd;
    if (p.hasta && today > p.hasta) return { label: "Vencida", live: false };
    if (p.desde && today < p.desde) return { label: "Programada", live: false };
    return { label: "Fuera de horario", live: false };
}
